const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://127.0.0.1:8000"
    : "");
const USER_ID = "1";

// -------------------------------
// Helpers
// -------------------------------
async function parseJsonOrText(res) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (e) {
    return { raw: text };
  }
}

function errWithDetails(action, res, details) {
  const msg = typeof details === "string" ? details : JSON.stringify(details);
  return new Error(`${action} failed: ${res.status} ${msg}`);
}

function authJsonHeaders() {
  return {
    "Content-Type": "application/json",
    "X-User-Id": USER_ID,
  };
}

function authHeaders() {
  return {
    "X-User-Id": USER_ID,
  };
}

// -------------------------------
// Backend status
// -------------------------------
export async function checkBackendStatus() {
  const res = await fetch(`${API_BASE}/status`, { method: "GET" });
  const data = await parseJsonOrText(res);
  if (!res.ok) throw errWithDetails("Status", res, data);
  return data;
}

// -------------------------------
// Upload project file
// -------------------------------
export async function uploadFile(file) {
  if (!file) throw new Error("Upload failed: No file selected");

  const form = new FormData();
  form.append("file", file);

  const res = await fetch(`${API_BASE}/upload`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });

  const data = await parseJsonOrText(res);
  if (!res.ok) throw errWithDetails("Upload", res, data);
  return data;
}

// -------------------------------
// Ingest
// -------------------------------
export async function ingestUpload(uploadId) {
  if (!uploadId) throw new Error("Ingest failed: Missing upload_id");

  const res = await fetch(`${API_BASE}/upload/ingest`, {
    method: "POST",
    headers: authJsonHeaders(),
    body: JSON.stringify({ upload_id: uploadId }),
  });

  const data = await parseJsonOrText(res);
  if (!res.ok) throw errWithDetails("Ingest", res, data);
  return data;
}

export async function getIngestedData(uploadId) {
  return ingestUpload(uploadId);
}

// -------------------------------
// Prepare LCA payload
// This now sends:
//   project_id
//   assets
// matching backend expectation
// -------------------------------
export function prepareLCAPayload(projectId, assets) {
  return {
    project_id: projectId,
    assets: Array.isArray(assets) ? assets : [],
  };
}
export async function createLcaRun({ project_id, scenario_id, scenario_name }) {
  if (!project_id) throw new Error("Create run failed: Missing project_id");
  if (!scenario_id) throw new Error("Create run failed: Missing scenario_id");

  const res = await fetch(`${API_BASE}/lca/create_run`, {
    method: "POST",
    headers: authJsonHeaders(),
    body: JSON.stringify({
      project_id,
      scenario_id,
      scenario_name,
    }),
  });

  const data = await parseJsonOrText(res);

  if (!res.ok && data?.error_code !== "RUN_ALREADY_EXISTS") {
    throw errWithDetails("Create run", res, data);
  }

  return data;
}
// -------------------------------
// Run LCA
// -------------------------------
export async function runLCA(payload) {
  if (!payload || typeof payload !== "object") {
    throw new Error("Run LCA failed: Missing payload");
  }

  const res = await fetch(`${API_BASE}/lca/calculate`, {
    method: "POST",
    headers: authJsonHeaders(),
    body: JSON.stringify(payload),
  });

  const data = await parseJsonOrText(res);

  if (!res.ok) {
    if (data?.error_code === "RUN_TIMEOUT") {
      return data;
    }

    throw errWithDetails("Run LCA", res, data);
  }

  return data;
}
// -------------------------------
// Run management
// -------------------------------
export async function getLcaRun(runId) {
  if (!runId) throw new Error("Fetch run failed: Missing run_id");

  const res = await fetch(`${API_BASE}/lca/get_run`, {
    method: "POST",
    headers: authJsonHeaders(),
    body: JSON.stringify({ run_id: runId }),
  });

  const data = await parseJsonOrText(res);
  if (!res.ok) throw errWithDetails("Fetch run", res, data);
  return data;
}
export async function lockLcaRun(runId) {
  if (!runId) throw new Error("Lock run failed: Missing run_id");

  const res = await fetch(`${API_BASE}/lca/lock_run`, {
    method: "POST",
    headers: authJsonHeaders(),
    body: JSON.stringify({ run_id: runId }),
  });

  const data = await parseJsonOrText(res);

  if (!res.ok) {
    throw errWithDetails("Lock run", res, data);
  }

  return data;
}
export async function listLcaRuns(projectId) {
  if (!projectId) throw new Error("List runs failed: Missing project_id");

  const res = await fetch(`${API_BASE}/lca/list_runs`, {
    method: "POST",
    headers: authJsonHeaders(),
    body: JSON.stringify({ project_id: projectId }),
  });

  const data = await parseJsonOrText(res);
  if (!res.ok) throw errWithDetails("List runs", res, data);
  return data;
}

export async function deleteLcaRun(runId) {
  if (!runId) throw new Error("Delete run failed: Missing run_id");

  const res = await fetch(`${API_BASE}/lca/delete_run`, {
    method: "POST",
    headers: authJsonHeaders(),
    body: JSON.stringify({ run_id: runId }),
  });

  const data = await parseJsonOrText(res);
  if (!res.ok) throw errWithDetails("Delete run", res, data);
  return data;
}

// -------------------------------
// Results
// -------------------------------
export async function getLcaResults(runId) {
  if (!runId) throw new Error("Results failed: Missing run_id");

  const res = await fetch(`${API_BASE}/lca/results/${encodeURIComponent(runId)}`, {
    method: "GET",
    headers: authHeaders(),
  });

  const data = await parseJsonOrText(res);
  if (!res.ok) throw errWithDetails("Results", res, data);
  return data;
}

// -------------------------------------------------
// Download PDF report
// -------------------------------------------------
export async function downloadReport(runId) {
  if (!runId) throw new Error("Report download failed: Missing run_id");

  const res = await fetch(
    `${API_BASE}/report/download/${encodeURIComponent(runId)}?t=${Date.now()}`,
    {
      method: "GET",
      cache: "no-store",
      headers: {
        ...authHeaders(),
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    }
  );

  if (!res.ok) {
    const details = await parseJsonOrText(res);
    throw errWithDetails("Report download", res, details);
  }

  return await res.blob();
}

// -------------------------------
// Debug
// -------------------------------
if (typeof window !== "undefined") {
  window.api = {
    checkBackendStatus,
    uploadFile,
    ingestUpload,
    getIngestedData,
    prepareLCAPayload,
    createLcaRun,
    runLCA,
    getLcaRun,
    lockLcaRun,
    listLcaRuns,
    deleteLcaRun,
    getLcaResults,
    downloadReport,
  };
}